import { Fragment, useContext } from "react";
import { RulesContext } from "../firebaseio/RulesContext";
import {
  BreadAccount,
  BreadInstitution,
  FirestoreDocCategory,
  getNullFieldValueCriterionEquivalent,
} from "breadcommon";
import { CategoriesContext } from "../firebaseio/CategoriesContext";
import { FirestoreDocRule } from "breadcommon";
import { VerticalSpacer } from "../utils/Utils";
import {
  firestoreDeleteRuleAndUpdateTransactions,
  firestoreUpdateRulesOrderAndUpdateTransactions,
} from "../firebaseio/firestoreIo";
import { UserContext } from "../firebaseio/UserContext";
import { InlineAccount } from "../common/InlineAccount";
import { RyeMainContentPanel } from "../rye/RyeMainContentPanel";
import { buildClasses } from "../utils/buildClasses";
import { RyeButton } from "../rye/RyeButton";
import { RyeDraggableList } from "../rye/RyeDraggableList";
import { RyeEmptyState } from "../rye/RyeEmptyState";
import { RyeSkeletonList } from "../rye/RyeSkeleton";
import { LoadingInitialDataContext } from "../LoadUser";

export function RulesScreen(): JSX.Element {
  const rules = useContext(RulesContext);
  const loading = useContext(LoadingInitialDataContext);
  const user = useContext(UserContext);
  const categories = useContext(CategoriesContext);

  const rulesList = Array.from(rules.values())
    .sort((a, b) => a.order_position - b.order_position)
    .map((rule, index) => {
      return {
        id: rule.id,
        value: (
          <Fragment>
            <div
              className={buildClasses(
                "flex",
                "justify-between",
                "items-center",
                "w-full",
                "rounded-lg",
                "p-7",
                "group/ruleitem",
              )}
              key={rule.id}
            >
              <RulePreview rule={rule} categories={categories} />
              <div
                className={buildClasses(
                  "invisible",
                  "group-hover/ruleitem:visible",
                )}
              >
                <RyeButton
                  icon="delete"
                  variant="transparent"
                  vibe="subdued"
                  size="sm"
                  tooltip="Delete rule"
                  onClick={() =>
                    firestoreDeleteRuleAndUpdateTransactions(user.uid, rule.id)
                  }
                />
              </div>
            </div>
            {index === rules.size ? <VerticalSpacer height={10} /> : null}
          </Fragment>
        ),
      };
    });

  return (
    <RyeMainContentPanel title="Rules" roundedTl={false} roundedBl={false}>
      {loading.rules || loading.categories ? (
        <RyeSkeletonList rowClassName="h-16" />
      ) : rules.size === 0 ? (
        <RyeEmptyState
          size="compact"
          icon="gavel"
          title="Categorize automatically with rules"
          description="Rules categorize new transactions for you automatically, based on things like their merchant, description, or account."
          note="To add a rule, categorize a transaction on the Transactions screen and then click the gavel icon to the right on that same transaction's row."
        />
      ) : (
        <>
          <RuleTips />
          <RyeDraggableList
            inputItems={rulesList}
            variant={"cards"}
            onOrderUpdate={function (newIdList: string[]): void {
              firestoreUpdateRulesOrderAndUpdateTransactions(
                user.uid,
                newIdList,
              );
            }}
          />
        </>
      )}
    </RyeMainContentPanel>
  );
}

export function RulePreview(props: {
  rule: FirestoreDocRule;
  categories: Map<string, FirestoreDocCategory>;
  accounts?: Map<string, BreadAccount> | undefined;
  institutions?: Map<string, BreadInstitution> | undefined;
}): JSX.Element {
  const categories = props.categories;
  const accounts = props.accounts;
  const institutions = props.institutions;
  const rule = props.rule;
  const category = categories.get(rule.action.category_id ?? "");
  const categoryString =
    category !== undefined
      ? `${category.emoji} ${category.name}`
      : "MISSING CATEGORY_ID";
  const isEmpty = (criterion_value: string): boolean => {
    return criterion_value === getNullFieldValueCriterionEquivalent();
  };
  const criteria = rule.criteria;
  return (
    <div>
      Assign the category <b>{categoryString}</b> if all of the following are
      true:
      <br />
      <ul className={buildClasses("list-disc", "list-inside")}>
        {criteria.account_id && (
          <li key="account_id">
            <div className={buildClasses("inline-flex", "items-center")}>
              <b>Account</b>
              <div className="w-2" />
              is
              <div className="w-2" />
              {isEmpty(criteria.account_id) ? (
                "empty"
              ) : (
                <InlineAccount
                  account_id={criteria.account_id}
                  logoGap={10}
                  accounts={accounts}
                  institutions={institutions}
                />
              )}
            </div>
          </li>
        )}
        {criteria.merchant_name && (
          <li key="merchant_name">
            <b>Merchant</b> is{" "}
            {isEmpty(criteria.merchant_name)
              ? "empty"
              : `exactly "${criteria.merchant_name}"`}
          </li>
        )}
        {criteria.type && (
          <li key="type">
            <b>Type</b> is {isEmpty(criteria.type) ? "empty" : criteria.type}
          </li>
        )}
        {criteria.subtype && (
          <li key="subtype">
            <b>Subtype</b> is{" "}
            {isEmpty(criteria.subtype) ? "empty" : criteria.subtype}
          </li>
        )}
        {criteria.description && (
          <li key="description">
            <b>Description</b> is{" "}
            {isEmpty(criteria.description)
              ? "empty"
              : `exactly "${criteria.description}"`}
          </li>
        )}
        {criteria.partial_description && (
          <li key="partial_description">
            <b>Description</b> contains "{criteria.partial_description}"
          </li>
        )}
      </ul>
    </div>
  );
}

function RuleTips(): JSX.Element {
  return (
    <div className={buildClasses("pb-10")}>
      <div
        className={buildClasses("flex", "items-center", "pb-2", "font-medium")}
      >
        <div>How rules work:</div>
      </div>
      <ul
        className={buildClasses("list-disc", "list-inside", "pl-3", "text-sm")}
      >
        <li>
          A Rule matches a Transaction if the Transaction meets the criteria of
          the Rule.
        </li>
        <li>
          On this page, rules are ordered from highest to lowest priority. If
          more than one Rule matches a transaction, the Rule that appears
          highest in the list below is used.
        </li>
        <li>
          Whenever a Rule is added, deleted, or modified, Unreviewed
          transactions are re-evaluated against the new set of rules.
        </li>
        <li>
          If a Transaction is already marked as Reviewed, then adding, deleting,
          or updating a Rule will not impact that Transaction.
        </li>
      </ul>
    </div>
  );
}
